import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Accueil", href: "#accueil" },
  { label: "Avantages", href: "#avantages" },
  { label: "Comment ça marche", href: "#fonctionnement" },
  { label: "Tarifs", href: "#tarifs" },
  { label: "Démo", href: "#inscription" },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/70 backdrop-blur-2xl border-b border-border/40 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.02)]"
          : "bg-transparent backdrop-blur-none"
      }`}
    >
      <nav className="container mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
        <a href="#accueil" className="group relative">
          <img
            src="/logos/logo-header-light.svg"
            alt="Medina Fidélité"
            className="h-[50px] dark:hidden transition-transform duration-300 group-hover:scale-105"
          />
          <img
            src="/logos/logo-header-dark.svg"
            alt="Medina Fidélité"
            className="h-[50px] hidden dark:block transition-transform duration-300 group-hover:scale-105"
          />
        </a>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="relative px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/50 transition-all duration-200 group"
            >
              {link.label}
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-primary rounded-full group-hover:w-4 transition-all duration-300" />
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="rounded-xl px-4"
            asChild
          >
            <a href="/login">Connexion</a>
          </Button>
          <Button
            size="sm"
            className="rounded-xl px-5 shadow-sm shadow-primary/15 hover:shadow-md hover:shadow-primary/25 transition-all duration-300"
            asChild
          >
            <a href="#inscription">Inscrire mon commerce</a>
          </Button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="h-5 w-5" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="h-5 w-5" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 top-16 bg-foreground/20 backdrop-blur-sm lg:hidden z-40"
              onClick={() => setIsMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(8px)" }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="absolute top-16 left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-border/60 shadow-lg lg:hidden z-50"
            >
              <div className="container mx-auto px-4 py-6 space-y-1">
                {navLinks.map((link, i) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="block px-4 py-3 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-all duration-200 font-medium"
                  >
                    {link.label}
                  </motion.a>
                ))}
                <div className="pt-4 mt-4 border-t border-border space-y-2">
                  <Button size="sm" variant="outline" className="w-full" asChild>
                    <a href="/login" onClick={() => setIsMenuOpen(false)}>
                      Connexion
                    </a>
                  </Button>
                  <Button size="sm" className="w-full shadow-sm shadow-primary/15" asChild>
                    <a href="#inscription" onClick={() => setIsMenuOpen(false)}>
                      Inscrire mon commerce
                    </a>
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};
